import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components/macro';
import { AnimatePresence, motion } from 'framer-motion';
import { observer } from 'mobx-react';

import { useDataStore } from 'state/DataStore';
import debounce from 'utilities/debounce';

const Screensaver = observer(() => {
    const [showScreensaver, setShowScreensaver] = useState(false);
    const screensaverTimeoutRef = useRef(null);
    const dataStore = useDataStore();
    
    // Get screensaver video URL from settings
    const videoUrl = dataStore.getSetting('general_settings', 'screensaver.url');
    
    // Screensaver logic
    useEffect(() => {
        const resetScreensaverTimer = () => {
            if (screensaverTimeoutRef.current) {
                clearTimeout(screensaverTimeoutRef.current);
            }
            
            setShowScreensaver(false);
            
            screensaverTimeoutRef.current = setTimeout(() => {
                setShowScreensaver(true);
            }, 5 * 60 * 1000); // 5 minutes
        };
        
        // Debounce user activity to improve performance
        const handleUserActivity = debounce(resetScreensaverTimer, 500);
        
        // Start the initial timer
        resetScreensaverTimer();
        
        // Add event listeners for user activity
        const events = [
            'mousemove',
            'mousedown',
            'keydown',
            'scroll',
            'touchstart',
            'click'
        ];
        
        events.forEach(event => {
            document.addEventListener(event, handleUserActivity, true);
        });
        
        return () => {
            if (screensaverTimeoutRef.current) {
                clearTimeout(screensaverTimeoutRef.current);
            }
            
            events.forEach(event => {
                document.removeEventListener(event, handleUserActivity, true);
            });
        };
    }, []);

    if (!videoUrl) return null;

    return createPortal(
        <AnimatePresence>
            {showScreensaver && (
                <ScreensaverOverlay 
                    as={motion.div}
                    onClick={() => setShowScreensaver(false)}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <Video
                        autoPlay
                        loop
                        muted
                        playsInline
                        src={videoUrl}
                    />
                </ScreensaverOverlay>
            )}
        </AnimatePresence>,
        document.body
    );
});

export default Screensaver;


const ScreensaverOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 9999;
    background-color: #000;
    cursor: pointer;
`;

const Video = styled.video`
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
`;
